// src/pages/account/ShowingProfile.js - ShowingBooker agent profile for property listings
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
  }
`;

const Button = styled.button`
  padding: 12px 20px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-right: 10px;
  
  &:hover {
    background: #0056b3;
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #666;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  }
`;

const InfoBox = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  padding: 15px;
  margin-top: 15px;
  font-size: 13px;
  color: #0066cc;
  line-height: 1.5;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const ShowingProfile = ({ user }) => {
  const [agentProfile, setAgentProfile] = useState({
    agent_name: '',
    agent_phone: '',
    agent_email: '',
    agent_license: '',
    agent_brokerage: ''
  });
  
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userEmail = user?.email || 'no-user';
    console.log(`🔄 [ShowingProfile] useEffect triggered for user: ${userEmail}`);
    
    if (user) {
      console.log(`🚀 [ShowingProfile] [${userEmail}] Triggering loadAgentProfile...`);
      loadAgentProfile();
    } else {
      console.log(`⚠️ [ShowingProfile] No user available, clearing agent profile state`);
      setAgentProfile({
        agent_name: '',
        agent_phone: '',
        agent_email: '',
        agent_license: '',
        agent_brokerage: ''
      });
    }
  }, [user]);

  const loadAgentProfile = async () => {
    try {
      const userEmail = user?.email || 'unknown';
      const userUID = user?.uid || 'unknown';
      console.log(`📋 [ShowingProfile] [${userEmail}] Loading agent profile from API... (UID: ${userUID})`);
      
      const token = await user.getIdToken();
      console.log(`📋 [ShowingProfile] [${userEmail}] Using token length: ${token.length}`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/showingbooker/agent/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const profileData = await response.json();
        setAgentProfile(profileData);
        console.log(`📋 [ShowingProfile] [${userEmail}] Loaded agent profile from API:`, profileData);
      } else {
        console.log(`📋 [ShowingProfile] [${userEmail}] No agent profile found or error loading profile`);
        // Keep empty state for new profiles
      }
    } catch (error) {
      console.error(`[ShowingProfile] Error loading agent profile for ${user?.email}:`, error);
      // Keep empty state if API fails
    }
  };

  const saveAgentProfile = async (data) => {
    try {
      const userEmail = user?.email || 'unknown';
      const userUID = user?.uid || 'unknown';
      console.log(`💾 [ShowingProfile] [${userEmail}] Saving agent profile to API:`, data, `(UID: ${userUID})`);
      
      const token = await user.getIdToken();
      console.log(`💾 [ShowingProfile] [${userEmail}] Using token length: ${token.length}`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/showingbooker/agent/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        const updatedProfile = await response.json();
        setAgentProfile(updatedProfile);
        console.log(`✅ [ShowingProfile] [${userEmail}] Agent profile saved to API:`, updatedProfile);
        return true;
      } else {
        const error = await response.json();
        console.error(`❌ [ShowingProfile] [${userEmail}] Failed to save agent profile:`, error);
        throw new Error(error.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error(`[ShowingProfile] Error saving agent profile for ${user?.email}:`, error);
      throw error;
    }
  };

  const updateAgentProfile = async () => {
    try {
      setSuccessMessage('');
      setErrorMessage('');
      
      // Basic validation
      if (!agentProfile.agent_name || !agentProfile.agent_phone) {
        setErrorMessage('Please fill in at least your name and phone number for the agent profile.');
        return;
      }
      
      await saveAgentProfile(agentProfile);
      setSuccessMessage('Agent Profile updated successfully! This information will appear on all your property booking pages.');
    } catch (error) {
      console.error('Error updating agent profile:', error);
      setErrorMessage('Failed to update agent profile: ' + (error.message || 'Unknown error'));
    }
  };

  const handleAgentProfileChange = (field, value) => {
    setAgentProfile({
      ...agentProfile,
      [field]: value
    });
  };

  return (
    <ProfileContainer>
      <h1>Agent Profile</h1>
      <p>Manage your agent contact information for ShowingBooker property listings and booking pages.</p>
      
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      
      <Section>
        <SectionTitle>🏠 ShowingBooker Contact Information</SectionTitle>
        
        <FormGroup>
          <Label>Full Name *</Label>
          <Input
            type="text"
            value={agentProfile.agent_name}
            onChange={(e) => handleAgentProfileChange('agent_name', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Phone Number *</Label>
          <Input
            type="tel"
            value={agentProfile.agent_phone}
            onChange={(e) => handleAgentProfileChange('agent_phone', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Email Address</Label>
          <Input
            type="email"
            value={agentProfile.agent_email}
            onChange={(e) => handleAgentProfileChange('agent_email', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>License Number</Label>
          <Input
            type="text"
            value={agentProfile.agent_license}
            onChange={(e) => handleAgentProfileChange('agent_license', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Brokerage (Optional)</Label>
          <Input
            type="text"
            value={agentProfile.agent_brokerage}
            onChange={(e) => handleAgentProfileChange('agent_brokerage', e.target.value)}
          />
        </FormGroup>

        <Button onClick={updateAgentProfile}>
          💾 Save Agent Profile
        </Button>
        
        <InfoBox>
          💡 <strong>This information appears on all your property booking pages</strong> so buyers and agents can contact you directly. Name and phone are required.
        </InfoBox>
      </Section>
    </ProfileContainer>
  );
};

export default ShowingProfile;